import { useDebouncedCallback } from 'use-debounce';
import * as React from 'react';
import { useWatch, Control, FieldValues } from 'react-hook-form';
import { areArraysDifferent } from '@/lib';
import { CaseDetailsLawyer } from '@/interfaces';
import { CompanyI, getMappedCompanies } from '@/components/dashboard';
import { useSession } from 'next-auth/react';
import { DEFAULT_CASE_NAME } from '@/components/dashboard/cases/create-case-form/consts';

interface Props {
	control: Control<FieldValues>;
	caseDetails: CaseDetailsLawyer | undefined;
	handleSave: () => void;
	autoSavedActived?: boolean;
}

interface CaseFormValues {
	name: string;
	caseType: string;
	category: string;
	status: string;
	lawyerIds: string[];
	elegibleStateIds: string[];
	companies: CompanyI[];
	targetGoals: string;
}

export const useAutoSaveCaseDetails = ({
	control,
	caseDetails,
	handleSave,
	autoSavedActived = true,
}: Props) => {
	const initialRender = React.useRef(true);
	const { data: user } = useSession();
	const userId = user?.user.user.id;
	const [isLoadingAutosaved, setIsLoadingAutosaved] = React.useState(false);

	React.useEffect(() => {
		/**
		 * Initialize lawyerIds with user.id if no caseDetails exist.
		 * Runs only when user data is ready and caseDetails aren't provided.
		 */
		if (userId && !caseDetails) {
			lastSavedData.current.lawyerIds = [userId];
		}
	}, [userId, caseDetails]);

	const watchedFields = useWatch({
		control,
		name: [
			'name',
			'caseType',
			'category',
			'companies',
			'elegibleStateIds',
			'lawyerIds',
			'status',
			'targetGoals',
		],
	});

	// Initialize with default or API data
	const lastSavedData = React.useRef<CaseFormValues>({
		name:
			caseDetails?.title === DEFAULT_CASE_NAME ? '' : caseDetails?.title || '',
		caseType: caseDetails?.case_type || '',
		category: caseDetails?.category_id?.toString() || '',
		companies: getMappedCompanies(caseDetails) || [],
		elegibleStateIds: caseDetails?.states?.map((s) => s.id?.toString()) || [],
		lawyerIds: caseDetails?.lawyers?.map((l) => l.id?.toString()) || [],
		status: caseDetails?.status?.id?.toString() || '',
		targetGoals: (caseDetails?.applicants_target_goal || '').toString() || '',
	});

	const normalizeIds = (items: any[]): string[] => {
		if (!Array.isArray(items)) return [];
		return typeof items[0] === 'string'
			? items
			: items.map((item) => item.name || item.id?.toString());
	};

	const areCompaniesDifferent = (
		company1: CompanyI[],
		company2: CompanyI[]
	): boolean => {
		if (company1.length < company2.length) return true;
		for (let i = 0; i < company1.length; i++) {
			const c1 = company1[i];
			const c2 = company2[i];
			if (
				(c1?.brandId && c1?.brandId !== c2?.brandId) ||
				(c1?.entityType && c1?.entityType !== c2?.entityType) ||
				(c1?.defendantIncorporation &&
					c1?.defendantIncorporation !== c2?.defendantIncorporation) ||
				(c1?.princpialPlaceOfBusiness &&
					c1?.princpialPlaceOfBusiness !== c2?.princpialPlaceOfBusiness)
			) {
				return true;
			}
		}
		return false;
	};

	const debouncedSave = useDebouncedCallback(() => {
		if (initialRender.current || !userId) {
			initialRender.current = false;
			setIsLoadingAutosaved(false);
			return;
		}

		const [
			name,
			caseType,
			category,
			companies,
			elegibleStateIds,
			lawyerIds,
			status,
			targetGoals,
		] = watchedFields;

		const statesIdsCurrent = normalizeIds(elegibleStateIds);
		const statesIdsLast = normalizeIds(lastSavedData.current.elegibleStateIds);

		const hasChanged =
			name !== lastSavedData.current.name ||
			caseType !== lastSavedData.current.caseType ||
			category !== lastSavedData.current.category ||
			status !== lastSavedData.current.status ||
			targetGoals?.toString() !==
				lastSavedData.current.targetGoals?.toString() ||
			areArraysDifferent(lawyerIds, lastSavedData.current.lawyerIds) ||
			areArraysDifferent(statesIdsCurrent, statesIdsLast) ||
			areCompaniesDifferent(companies, lastSavedData.current.companies);

		if (!hasChanged) {
			setIsLoadingAutosaved(false);
			return;
		}

		// console.log(
		// 	name !== lastSavedData.current.name,
		// 	caseType !== lastSavedData.current.caseType,
		// 	category !== lastSavedData.current.category,
		// 	status !== lastSavedData.current.status,
		// 	targetGoals?.toString() !== lastSavedData.current.targetGoals?.toString(),
		// 	areArraysDifferent(lawyerIds, lastSavedData.current.lawyerIds),
		// 	areArraysDifferent(statesIdsCurrent, statesIdsLast),
		// 	areCompaniesDifferent(companies, lastSavedData.current.companies)
		// );

		try {
			// Save the new data
			handleSave();

			// Update with the current data
			lastSavedData.current = {
				name,
				caseType,
				category,
				companies,
				elegibleStateIds,
				lawyerIds,
				status,
				targetGoals,
			};
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoadingAutosaved(false);
		}
	}, 1000);

	// run debounce every time the data changes
	React.useEffect(() => {
		// Autosave disabled
		if (!autoSavedActived) return;

		setIsLoadingAutosaved(true);
		debouncedSave();
	}, [watchedFields]);

	return { isLoadingAutosaved, setIsLoadingAutosaved };
};
